import { FormControlLabel, FormGroup, FormLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { AppImageDimension } from 'app/appImages';
import { MprcContainer } from "components/MprcContainer";
import { ImageSelectionFit } from 'components/MprcImageHandler';
import { MprcInfiniteLoading } from "components/MprcInfiniteLoading";
import { useMprcTheme } from "components/MprcThemeProvider";
import { MprcFilterBox, MprcFilterBoxContent } from "components/filter";
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import ReactCrop, { Crop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { ImageHandlerStyles, MaxHeightImageContainer, MaxWidthImageContainer } from "./ImageHandlerStyles";
import { ConvertUrlToBase64Result, ImageUtils, Resizer } from "./ImageResizer";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 100,
        height: 100
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IEditImageState {
  image?: string;
  originalSize: ImageDimension;
}

interface IEditImageProps {
  src: string;
  fallbackUrl?: string;
  dimension: ImageDimension;
  mimetype: string;
  backgroundColor: string;
  fitMode?: ImageSelectionFit;
  onChange: (data: ConvertUrlToBase64Result) => void;
  onError: (message: string) => void;
}

export const EditImage: React.FC<IEditImageProps> = (props) => {
  const { dimension, backgroundColor, fitMode = "default" } = props;
  const { theme } = useMprcTheme();
  const { classes, cx } = ImageHandlerStyles();
  const [rotate, setRotate] = useState(0);
  const [aspect] = useState<number>(dimension.width / dimension.height);
  const [option, setOption] = useState<string>("crop");
  const [crop, setCrop] = useState<Crop>();
  const [finalCrop, setFinalCrop] = useState<Crop>();
  const [previewImage, setPreviewImage] = useState<ConvertUrlToBase64Result>();
  const [state, setState] = useState<IEditImageState>({ originalSize: AppImageDimension.ImageEditor });

  const imgRef = useRef<HTMLImageElement>(null);

  const handleOptionChange = (_: React.ChangeEvent<{}>, value: string) => {
    setOption(value);
  };

  const changeResult = (dataUrl: ConvertUrlToBase64Result) => {
    if (previewImage && previewImage.dataUrl) ImageUtils.revokeObjectUrlIfBlob(previewImage.dataUrl);
    setPreviewImage(dataUrl);
    props.onChange(dataUrl);
  };

  const resizeImage = (imageElement: HTMLImageElement) => {
    var r = new Resizer();
    if (fitMode == "cover")
      r.createCoverImage(imageElement, dimension, "image/png", 100, rotate, (data) => changeResult(data));
    else
      r.createResizedImage(imageElement, dimension, "image/png", 100, rotate, backgroundColor, (data) => changeResult(data));
  };

  //#region Redimensionar Imagem

  const onLoadImageToRedim = (imageElement: HTMLImageElement) => {
    if (imageElement.width > imageElement.height) {
      imageElement.style.maxWidth = "100%";
    }
    else {
      imageElement.style.maxHeight = "100%";
    }
    resizeImage(imageElement);
  };

  const handleRotateChange = (event: React.ChangeEvent<{}>, value: string) => {
    setRotate(parseInt(value));
  };

  useEffect(() => {
    resizeImage(document.getElementById("imgResize") as HTMLImageElement);
  }, [rotate]);


  useEffect(() => {
    if (finalCrop) makeClientCrop(finalCrop);
  }, [finalCrop]);

  //#endregion

  //#region Cortar Imagem

  const onCropImageLoaded = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspect));
  };

  const makeClientCrop = (crop: Crop) => {
    if (imgRef.current && crop.width && crop.height) {
      var img = new Image();
      img.src = getCroppedImg(imgRef.current, crop);
      img.onload = () => {
        resizeImage(img);
      };
      img.onerror = (e) => {
        props.onError(theme.MprcImageSelection.editCropError);
      };
    }
  };

  const getCroppedImg = (image: HTMLImageElement, crop: Crop): string => {
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement("canvas");
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");
    ctx?.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      canvas.width,
      canvas.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return canvas.toDataURL("image/png", 100);
  };

  //#endregion

  ///
  useEffect(() => {
    ImageUtils.readImage(props.src, props.mimetype)
      .then((convert) => {
        setState({
          ...state,
          image: convert.dataUrl,
          originalSize: {
            width: convert.width,
            height: convert.height
          }
        });
      })
      .catch((e) => {
        if (props.fallbackUrl) {
          ImageUtils.readImage(props.fallbackUrl, props.mimetype)
            .then((convert) => {
              setState({
                ...state,
                image: convert.dataUrl,
                originalSize: {
                  width: convert.width,
                  height: convert.height
                }
              });
              enqueueSnackbar(theme.MprcImageSelection.editImageFallbackSuccess, { variant: "warning" });
            })
            .catch((e) => {
              props.onError(theme.MprcImageSelection.editImageLoadError);
            });
        }
        else props.onError(theme.MprcImageSelection.editImageLoadError);
      });

    return () => {
      if (state.image) ImageUtils.revokeObjectUrlIfBlob(state.image);
      if (previewImage) ImageUtils.revokeObjectUrlIfBlob(previewImage.dataUrl);
    };
  }, []);

  /////////////////////////////////////////////////////////////////////////////

  if (state.image == null)
    return <MprcInfiniteLoading visible />;


  const canBeCropped = true;// (image.width > width || image.height > height);

  const p = (state.originalSize.width > state.originalSize.height)
    ? state.originalSize.width / MaxWidthImageContainer
    : state.originalSize.height / MaxHeightImageContainer;

  const maxW = Math.ceil(state.originalSize.width / p),
    maxH = Math.ceil(state.originalSize.height / p),
    min = Math.min(maxW, maxH);

  return (
    <>
      <MprcContainer>
        <MprcFilterBox>
          <MprcFilterBoxContent
            title={theme.MprcImageSelection.editTitle}
          >
            <Grid container justifyContent="space-between">
              {
                canBeCropped &&
                <Grid>
                  <FormGroup>
                    <RadioGroup
                      row
                      name="method"
                      value={option}
                      onChange={handleOptionChange}
                    >
                      <FormControlLabel
                        value="crop"
                        control={<Radio color="primary" />}
                        label={theme.MprcImageSelection.cropText}
                      />

                      <FormControlLabel
                        value="resize"
                        control={<Radio color="primary" />}
                        label={theme.MprcImageSelection.resizeText}
                      />
                    </RadioGroup>
                  </FormGroup>
                </Grid>
              }

              {
                canBeCropped && option == "resize" &&
                <Grid>
                  <FormGroup row style={{ alignItems: "center" }}>
                    <FormLabel>{theme.MprcImageSelection.rotationText}</FormLabel>
                    <RadioGroup
                      row
                      name="rotate"
                      value={rotate.toString()}
                      style={{ marginLeft: 8 }}
                      onChange={handleRotateChange}

                    >
                      <FormControlLabel
                        value="0"
                        control={<Radio color="primary" />}
                        label="0"
                      />
                      <FormControlLabel
                        value="90"
                        control={<Radio color="primary" />}
                        label="90"
                      />
                      <FormControlLabel
                        value="180"
                        control={<Radio color="primary" />}
                        label="180"
                      />
                      <FormControlLabel
                        value="270"
                        control={<Radio color="primary" />}
                        label="270"
                      />
                    </RadioGroup>
                  </FormGroup>
                </Grid>
              }
            </Grid>
          </MprcFilterBoxContent>

        </MprcFilterBox>
      </MprcContainer>

      <Grid container spacing={1} alignItems="center">
        <Grid size={{ xs: 6 }}>
          <div className={cx(classes.imageBorder, classes.imageContainer)} >
            <div className={classes.imageBoxArea}>
              <Grid container justifyContent="center" alignItems="center" height="100%">
                <Grid alignSelf="center">
                  {
                    option == "resize"
                      ? (
                        <img
                          id="imgResize"
                          src={state.image}
                          style={
                            {
                              maxHeight: aspect == 1 ? min : maxH,
                              maxWidth: aspect == 1 ? min : maxW
                            }
                          }
                          onLoad={() => onLoadImageToRedim(document.getElementById("imgResize") as HTMLImageElement)}
                        />
                      )
                      : (
                        <ReactCrop
                          crop={crop}
                          onChange={(fixedCrop, percentCrop) => setCrop(fixedCrop)}
                          onComplete={(fixedCrop, percentCrop) => setFinalCrop(fixedCrop)}
                          aspect={aspect}
                        >
                          <img
                            ref={imgRef}
                            src={state.image}
                            onLoad={onCropImageLoaded}
                            style={
                              {
                                maxHeight: maxH,
                                maxWidth: maxW
                              }
                            }
                          />
                        </ReactCrop>
                      )
                  }
                </Grid>
              </Grid>
            </div>
          </div>
          <Typography variant="subtitle1" color="black" component="p">
            {theme.MprcImageSelection.originalSize}: {state.originalSize?.width} x {state.originalSize?.height}
          </Typography>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <div className={cx(classes.imageBorder, classes.imageContainer)} >

            <div className={classes.imageBoxArea}>
              <Grid container justifyContent="center" alignItems="center" height="100%">
                <Grid alignSelf="center" style={{ height: previewImage && previewImage.width < previewImage.height && previewImage.height > MaxHeightImageContainer ? "100%" : undefined }}>
                  {
                    previewImage &&
                    <img
                      id="previewImageSelection"
                      src={previewImage.dataUrl}
                      className={previewImage.width > previewImage.height ? classes.previewImageW : classes.previewImageH}
                    />
                  }
                </Grid>
              </Grid>
            </div>
          </div>
          <Typography variant="subtitle1" color="black" component="p">
            {theme.MprcImageSelection.finalSize}: {dimension.width} x {dimension.height}
          </Typography>

        </Grid>
      </Grid>
    </>
  );
};
